// ─── src/components/reader/ReaderChrome.jsx ─────────────────────────────────
// The reader's furniture: the top bar and the contents panel, shared by every
// flipbook surface so they are the same object rather than two that look alike.
//
// These were defined inside PublicationsReaderPage. When the private Membership
// Edition needed a toolbar it got a second, hand-written one, and the two drifted
// immediately: different height, different buttons, different type. Lifting them
// here means a change to the reader chrome reaches both, which is the whole point.
//
// Surfaces differ in which tools apply, so the visible set is a prop rather than a
// fork: a private per-recipient edition has no "copy share link" (it would undo the
// tracking it exists for) and nothing to search or export, but it is otherwise the
// identical bar.
import { useEffect, useRef } from 'react';
import { imgUrl } from '../../utils/imgUrl';

export const GOLD = '#d39a21';
export const GD   = "var(--font-heading-primary, 'Cormorant Garamond', Georgia, serif)";
export const NU   = "var(--font-body, 'Jost', sans-serif)";

export const READER_THEMES = {
  dark: {
    bg: '#080706', pageBg: '#0F0D0A',
    ctrlBg: 'rgba(0,0,0,0.6)', ctrlText: 'rgba(255,255,255,0.72)', ctrlBdr: 'rgba(255,255,255,0.1)',
    muted: 'rgba(255,255,255,0.38)',
    topGrad: 'linear-gradient(to bottom, rgba(8,7,6,0.96) 0%, rgba(8,7,6,0) 100%)',
    btnHov: 'rgba(211,154,33,0.18)', text: '#ffffff',
    shadow: '0 8px 48px rgba(0,0,0,0.6)', pageShad: '0 8px 48px rgba(0,0,0,0.6)',
  },
  light: {
    bg: '#EAE5DC', pageBg: '#FFFFFF',
    ctrlBg: 'rgba(255,255,255,0.88)', ctrlText: 'rgba(24,18,10,0.72)', ctrlBdr: 'rgba(24,18,10,0.12)',
    muted: 'rgba(24,18,10,0.45)',
    topGrad: 'linear-gradient(to bottom, rgba(234,229,220,0.97) 0%, rgba(234,229,220,0) 100%)',
    btnHov: 'rgba(211,154,33,0.15)', text: '#18120A',
    shadow: '0 4px 28px rgba(0,0,0,0.12)', pageShad: '0 8px 40px rgba(0,0,0,0.14)',
  },
};

// Which tools a surface offers. Everything defaults ON so the publications reader
// keeps exactly the bar it had.
const ALL_TOOLS = {
  back: true, save: true, share: true, export: true,
  search: true, textMode: true, lights: true, download: true,
};

// ── Top bar ─────────────────────────────────────────────────────────────────
export function ReaderTopBar({
  issue, onBack, textEditionSlug, onDownload,
  showTOC, onToggleTOC,
  bookmarked, onToggleBookmark,
  onShare, onExport, onSearch, onTextMode,
  readerMode, onToggleMode,
  tools, kicker, onDownloadTrack,
  T,
}) {
  const t = { ...ALL_TOOLS, ...(tools || {}) };
  const btnStyle = {
    background: T.ctrlBg, border: `1px solid ${T.ctrlBdr}`, borderRadius: 2,
    padding: '6px 12px', color: T.ctrlText, fontFamily: NU, fontSize: 9,
    fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
    cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
    display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
  };

  return (
    <div style={{
      position: 'fixed', top: 3, left: 0, right: 0, zIndex: 100, height: 52,
      background: T.topGrad, display: 'flex', alignItems: 'center',
      padding: '0 20px', gap: 10,
    }}>
      {t.back && (
        <button onClick={onBack} title="Back to publications"
          style={{ ...btnStyle, width: 36, height: 36, padding: 0,
                   justifyContent: 'center', borderRadius: '50%', fontSize: 16 }}>
          ←
        </button>
      )}

      {/* Issue info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: NU, fontSize: 9, fontWeight: 600, color: GOLD,
                      letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 2 }}>
          {kicker !== undefined
            ? kicker
            : [issue?.issue_number && `Issue ${issue.issue_number}`, issue?.season]
                .filter(Boolean).join(' · ')}
        </div>
        <h1 style={{ fontFamily: GD, fontSize: 16, fontWeight: 400, color: T.text,
                     whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', margin: 0 }}>
          {issue?.title || 'Untitled Issue'}
        </h1>
      </div>

      <button onClick={onToggleTOC}
        style={{ ...btnStyle, color: showTOC ? GOLD : T.ctrlText,
                 border: showTOC ? '1px solid rgba(211,154,33,0.45)' : `1px solid ${T.ctrlBdr}` }}>
        ☰ Contents
      </button>

      {/* The readable, hyperlinked article companion to the flip issue */}
      {textEditionSlug && (
        <a href={`/lwd-edit/${textEditionSlug}`}
          style={{ ...btnStyle, textDecoration: 'none', color: GOLD,
                   border: '1px solid rgba(211,154,33,0.45)', background: 'rgba(211,154,33,0.08)' }}
          title="Read the full text edition, readable, with every venue linked">
          ¶ Read
        </a>
      )}

      {t.save && (
        <button onClick={onToggleBookmark}
          style={{ ...btnStyle, color: bookmarked ? GOLD : T.ctrlText,
                   border: bookmarked ? '1px solid rgba(211,154,33,0.45)' : `1px solid ${T.ctrlBdr}` }}
          title={bookmarked ? 'Remove bookmark' : 'Bookmark this page'}>
          {bookmarked ? '✦ Saved' : '◇ Save'}
        </button>
      )}

      {t.share && (
        <button onClick={onShare} style={{ ...btnStyle, display: 'none' }}
          className="pub-reader-share" title="Copy share link">↗ Share</button>
      )}
      {t.export && (
        <button onClick={onExport} style={{ ...btnStyle, display: 'none' }}
          className="pub-reader-export" title="Export this page as a social-media image">⬇ Export Page</button>
      )}
      {t.search && (
        <button onClick={onSearch} style={{ ...btnStyle, display: 'none' }}
          className="pub-reader-search" title="Search issue (press /)">🔍 Search</button>
      )}
      {t.textMode && (
        <button onClick={onTextMode} style={{ ...btnStyle, display: 'none' }}
          className="pub-reader-textmode" title="Accessibility text mode (press A)">Aa Text</button>
      )}
      {t.lights && (
        <button onClick={onToggleMode} style={{ ...btnStyle, display: 'none' }}
          className="pub-reader-mode" title="Toggle reader mode">
          {readerMode === 'dark' ? '☀ Lights On' : '☾ Lights Off'}
        </button>
      )}

      <div className="pub-reader-brand"
        style={{ fontFamily: GD, fontSize: 14, fontWeight: 400, fontStyle: 'italic',
                 color: GOLD, letterSpacing: '0.04em', flexShrink: 0, display: 'none' }}>
        LWD
      </div>

      {/* The private edition's PDF is a signed URL rather than a click handler, so
          the button is an anchor there and a button in the publications reader. */}
      {t.download && onDownload && (issue?.pdf_url || issue?.pdf) && (
        typeof onDownload === 'string'
          ? <a href={onDownload} onClick={onDownloadTrack} className="pub-reader-dl"
               title="Download the full magazine as a PDF"
               style={{ ...btnStyle, display: 'none', textDecoration: 'none' }}>
              ↓ Download Issue
            </a>
          : <button onClick={onDownload} title="Download the full magazine as a PDF"
               style={{ ...btnStyle, display: 'none' }} className="pub-reader-dl">
              ↓ Download Issue
            </button>
      )}

      <style>{`
        @media (min-width: 640px) {
          .pub-reader-brand    { display: block !important; }
          .pub-reader-dl       { display: flex !important; }
          .pub-reader-share    { display: flex !important; }
          .pub-reader-export   { display: flex !important; }
          .pub-reader-search   { display: flex !important; }
          .pub-reader-textmode { display: flex !important; }
          .pub-reader-mode     { display: flex !important; }
        }
      `}</style>
    </div>
  );
}

// ── Contents panel ──────────────────────────────────────────────────────────
export function ReaderTOCPanel({
  open, onClose, pages, currentPage, onJump,
  bookmarks = [], onToggleBookmark, showBookmarks = true, T,
}) {
  const listRef = useRef(null);
  const activeRef = useRef(null);

  useEffect(() => {
    if (open && activeRef.current && listRef.current) {
      activeRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [open, currentPage]);

  return (
    <>
      {open && (
        <div onClick={onClose}
          style={{ position: 'fixed', inset: 0, zIndex: 190, background: 'rgba(0,0,0,0.4)' }} />
      )}
      <div style={{
        position: 'fixed', top: 0, left: 0, bottom: 0, width: 280, zIndex: 195,
        background: T.ctrlBg, backdropFilter: 'blur(12px)',
        borderRight: `1px solid ${T.ctrlBdr}`, display: 'flex', flexDirection: 'column',
        transform: open ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform 0.28s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '18px 16px 14px', borderBottom: `1px solid ${T.ctrlBdr}`, flexShrink: 0 }}>
          <span style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, color: GOLD,
                         letterSpacing: '0.18em', textTransform: 'uppercase' }}>Contents</span>
          <button onClick={onClose}
            style={{ background: 'none', border: 'none', color: T.muted,
                     cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 4 }}>✕</button>
        </div>

        <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {pages.map(p => {
            const isCurrent = p.page_number === currentPage;
            const isBookmarked = bookmarks.includes(p.page_number);
            const thumbSrc = imgUrl(p.thumbnail_url || p.image_url, { width: 300 }) || null;
            return (
              <div key={p.page_number} ref={isCurrent ? activeRef : null}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '6px 12px 6px 0',
                  borderLeft: isCurrent ? `3px solid ${GOLD}` : '3px solid transparent',
                  paddingLeft: isCurrent ? 13 : 16, cursor: 'pointer',
                  background: isCurrent ? 'rgba(211,154,33,0.06)' : 'transparent',
                  transition: 'background 0.15s',
                }}
                onClick={() => { onJump(p.page_number); onClose(); }}
                onMouseEnter={e => { if (!isCurrent) e.currentTarget.style.background = T.btnHov; }}
                onMouseLeave={e => { if (!isCurrent) e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ width: 48, height: 68, flexShrink: 0, background: T.pageBg,
                              borderRadius: 1, overflow: 'hidden',
                              border: `1px solid ${isCurrent ? 'rgba(211,154,33,0.4)' : T.ctrlBdr}` }}>
                  {thumbSrc
                    ? <img src={thumbSrc} alt={`p${p.page_number}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <div style={{ width: '100%', height: '100%', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    fontFamily: NU, fontSize: 9, color: T.muted }}>{p.page_number}</div>}
                </div>
                <span style={{ fontFamily: NU, fontSize: 10, color: isCurrent ? T.text : T.muted,
                               flex: 1, letterSpacing: '0.04em' }}>
                  Page {p.page_number}
                </span>
                {showBookmarks && (
                  <button onClick={e => { e.stopPropagation(); onToggleBookmark(p.page_number); }}
                    title={isBookmarked ? 'Remove bookmark' : 'Bookmark this page'}
                    style={{ background: 'none', border: 'none', cursor: 'pointer',
                             color: isBookmarked ? GOLD : T.muted, fontSize: 13,
                             padding: '4px 8px', flexShrink: 0, transition: 'color 0.15s' }}>
                    {isBookmarked ? '✦' : '◇'}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
